import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InfoIcon from "@mui/icons-material/Info";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import {userGetBuildVersionCompatibility} from "../../../components/business_logic/build_version_compatibility_requests";
import {Build, BuildTrackChoices, BuildPlatformChoices, CompatibilityMatrix} from "../../../models/build";
import {RootState} from "../../../store/store";
import PreviewBuild from "./preview_build";
import BuildCard from "./build_card";

export default function BuildCompatibilityCatalog() {
    const currentUser = useSelector((state: RootState) => state.session.user);
    const [compatibleBuilds, setCompatibleBuilds] = useState<CompatibilityMatrix[]>([]);
    const [builds, setBuilds] = useState<Build[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    // Filter state
    const [serverVersionFilter, setServerVersionFilter] = useState<string>('');
    const [clientVersionFilter, setClientVersionFilter] = useState<string>('');
    const [trackFilter, setTrackFilter] = useState<string>('');
    const [platformFilter, setPlatformFilter] = useState<string>('');
    
    // Track which row is expanded
    const [expandedRow, setExpandedRow] = useState<string | null>(null);
    
    // Preview state
    const [selectedBuild, setSelectedBuild] = useState<Build | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    useEffect(() => {
        loadCompatibilityData().then();
    }, []);

    async function loadCompatibilityData() {
        setLoading(true);
        setError(null);
        try {
            const response = await userGetBuildVersionCompatibility();
            
            if (response && Object.keys(response).length > 0) {
                setCompatibleBuilds(response.compatible_builds || []);
                setBuilds(response.builds || []);
            } else {
                setCompatibleBuilds([]);
                setBuilds([]);
            }
        } catch (err) {
            setError('Failed to load compatibility data. Please try again later.');
            console.error('Error loading compatibility data:', err);
        } finally {
            setLoading(false);
        }
    }

    // Check user permissions for track access
    const hasDevAccess = currentUser.groups?.some(group => 
        group.name === 'Developer' || group.name === 'developer'
    ) || false;
    
    const hasBetaAccess = currentUser.groups?.some(group => 
        group.name === 'Beta Tester' || group.name === 'beta_tester' || group.name === 'Beta Tester Group'
    ) || hasDevAccess; // Dev users also have beta access
    
    // Get allowed tracks based on permissions
    const getAllowedTracks = (): string[] => {
        if (hasDevAccess) {
            return ['stable', 'beta', 'dev'];
        } else if (hasBetaAccess) {
            return ['stable', 'beta'];
        } else {
            return ['stable'];
        }
    };
    
    const allowedTracks = getAllowedTracks();
    
    // Check if a build track is allowed for the user
    const isTrackAllowed = (track: string): boolean => {
        return allowedTracks.includes(track);
    };

    // Function to compare version numbers (semantic versioning)
    const compareVersions = (a: string, b: string): number => {
        const parseVersion = (version: string): number[] => {
            const cleaned = version.replace(/[^0-9.-]/g, '');
            const parts = cleaned.split('.').map(part => {
                const numPart = part.split('-')[0];
                return parseInt(numPart, 10) || 0;
            });
            while (parts.length < 3) {
                parts.push(0);
            }
            return parts;
        };

        const aParts = parseVersion(a);
        const bParts = parseVersion(b);

        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
            const aPart = aParts[i] || 0;
            const bPart = bParts[i] || 0;
            if (aPart > bPart) return -1; // Descending order
            if (aPart < bPart) return 1;
        }
        return 0;
    };

    // Filter builds by track permissions
    const allowedBuilds = builds.filter(b => isTrackAllowed(b.track));
    
    // Get unique version numbers from builds, filtered by component and track permissions, then sorted
    const serverVersions = Array.from(new Set(
        allowedBuilds
            .filter(b => b.component === 'server')
            .map(b => b.version_number)
    )).sort(compareVersions);
    
    const clientVersions = Array.from(new Set(
        allowedBuilds
            .filter(b => b.component === 'client')
            .map(b => b.version_number)
    )).sort(compareVersions);

    // Filter compatible builds based on selected filters and track permissions
    const filteredCompatibleBuilds = compatibleBuilds.filter(record => {
        const serverBuild = builds.find(b => b.id === record.server_build);
        const clientBuild = builds.find(b => b.id === record.client_build);
        
        // Filter out records where either build has a track the user doesn't have permission for
        if (serverBuild && !isTrackAllowed(serverBuild.track)) {
            return false;
        }
        if (clientBuild && !isTrackAllowed(clientBuild.track)) {
            return false;
        }
        
        const serverVersion = serverBuild?.version_number || '';
        const clientVersion = clientBuild?.version_number || '';
        
        const matchesServer = !serverVersionFilter || serverVersion === serverVersionFilter;
        const matchesClient = !clientVersionFilter || clientVersion === clientVersionFilter;
        
        // Check track filter - match if either server or client build matches
        const matchesTrack = !trackFilter || 
            serverBuild?.track === trackFilter || 
            clientBuild?.track === trackFilter;
        
        // Check platform filter - match if either server or client build matches
        const matchesPlatform = !platformFilter || 
            serverBuild?.platform === platformFilter || 
            clientBuild?.platform === platformFilter;
        
        return matchesServer && matchesClient && matchesTrack && matchesPlatform;
    });

    // Sort filtered builds
    const sortedCompatibleBuilds = [...filteredCompatibleBuilds].sort((a, b) => {
        const aServerBuild = allowedBuilds.find(build => build.id === a.server_build);
        const bServerBuild = allowedBuilds.find(build => build.id === b.server_build);
        const aClientBuild = allowedBuilds.find(build => build.id === a.client_build);
        const bClientBuild = allowedBuilds.find(build => build.id === b.client_build);
        
        const aServerVersion = aServerBuild?.version_number || '';
        const bServerVersion = bServerBuild?.version_number || '';
        const aClientVersion = aClientBuild?.version_number || '';
        const bClientVersion = bClientBuild?.version_number || '';
        
        // Sort by server version first (descending), then client version (descending)
        const serverCompare = compareVersions(aServerVersion, bServerVersion);
        if (serverCompare !== 0) return serverCompare;
        return compareVersions(aClientVersion, bClientVersion);
    });

    // Get related compatible builds for a given record - returns server and client builds separately
    const getRelatedCompatibleBuilds = (record: CompatibilityMatrix): {
        compatibleServerBuilds: Build[];
        compatibleClientBuilds: Build[];
    } => {
        // Get the current record's version numbers
        const currentServerBuild = builds.find(b => b.id === record.server_build);
        const currentClientBuild = builds.find(b => b.id === record.client_build);
        const currentServerVersion = currentServerBuild?.version_number || '';
        const currentClientVersion = currentClientBuild?.version_number || '';
        
        // Find all server builds compatible with the current client version
        const compatibleServerBuildIds = new Set<string>();
        compatibleBuilds.forEach(relatedRecord => {
            const relatedClientBuild = builds.find(b => b.id === relatedRecord.client_build);
            
            if (relatedClientBuild?.version_number === currentClientVersion && relatedRecord.compatible) {
                // Add the server build ID
                compatibleServerBuildIds.add(relatedRecord.server_build);
            }
        });
        
        // Find all client builds compatible with the current server version
        const compatibleClientBuildIds = new Set<string>();
        compatibleBuilds.forEach(relatedRecord => {
            const relatedServerBuild = builds.find(b => b.id === relatedRecord.server_build);
            
            if (relatedServerBuild?.version_number === currentServerVersion && relatedRecord.compatible) {
                // Add the client build ID
                compatibleClientBuildIds.add(relatedRecord.client_build);
            }
        });
        
        // Get the actual build objects and filter by component and track permissions
        const compatibleServerBuilds = allowedBuilds
            .filter(b => b.component === 'server' && compatibleServerBuildIds.has(b.id))
            .sort((a, b) => compareVersions(a.version_number, b.version_number));
        
        const compatibleClientBuilds = allowedBuilds
            .filter(b => b.component === 'client' && compatibleClientBuildIds.has(b.id))
            .sort((a, b) => compareVersions(a.version_number, b.version_number));
        
        return { compatibleServerBuilds, compatibleClientBuilds };
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    const handleBuildClick = (build: Build) => {
        setSelectedBuild(build);
        setPreviewOpen(true);
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
        setSelectedBuild(null);
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                {/* Header Section */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mb: 3,
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                        Download Center
                    </Typography>
                </Box>

                {/* Information Section */}
                <Card variant="outlined" sx={{ mb: 4, backgroundColor: 'action.hover' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <InfoIcon sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                    Available Downloads
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Browse and download available builds for PennyPusher. Filter by component (server/client), platform, track, or version 
                                    to find the build you need. The table below shows compatible build combinations - expand any row to see related compatible builds.
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {/* Filters */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Filter Server Version</InputLabel>
                        <Select
                            value={serverVersionFilter}
                            label="Filter Server Version"
                            onChange={(e) => setServerVersionFilter(e.target.value)}
                        >
                            <MenuItem value="">
                                <em>All</em>
                            </MenuItem>
                            {serverVersions.map((version) => (
                                <MenuItem key={version} value={version}>
                                    {version}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Filter Client Version</InputLabel>
                        <Select
                            value={clientVersionFilter}
                            label="Filter Client Version"
                            onChange={(e) => setClientVersionFilter(e.target.value)}
                        >
                            <MenuItem value="">
                                <em>All</em>
                            </MenuItem>
                            {clientVersions.map((version) => (
                                <MenuItem key={version} value={version}>
                                    {version}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Filter Track</InputLabel>
                        <Select
                            value={trackFilter}
                            label="Filter Track"
                            onChange={(e) => setTrackFilter(e.target.value)}
                        >
                            <MenuItem value="">
                                <em>All</em>
                            </MenuItem>
                            {BuildTrackChoices
                                .filter(track => isTrackAllowed(track.value))
                                .map((track) => (
                                    <MenuItem key={track.value} value={track.value}>
                                        {track.label}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                    
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Filter Platform</InputLabel>
                        <Select
                            value={platformFilter}
                            label="Filter Platform"
                            onChange={(e) => setPlatformFilter(e.target.value)}
                        >
                            <MenuItem value="">
                                <em>All</em>
                            </MenuItem>
                            {BuildPlatformChoices.map((platform) => (
                                <MenuItem key={platform.value} value={platform.value}>
                                    {platform.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    {(serverVersionFilter || clientVersionFilter || trackFilter || platformFilter) && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography 
                                variant="body2" 
                                color="text.secondary"
                                onClick={() => {
                                    setServerVersionFilter('');
                                    setClientVersionFilter('');
                                    setTrackFilter('');
                                    setPlatformFilter('');
                                }}
                                sx={{ 
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    '&:hover': { color: 'primary.main' }
                                }}
                            >
                                Clear Filters
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Compatibility Table */}
                {sortedCompatibleBuilds.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">
                            No compatible builds found.
                        </Typography>
                        {(serverVersionFilter || clientVersionFilter) && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Try adjusting your filters to see more results.
                            </Typography>
                        )}
                    </Paper>
                ) : (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="50px"></TableCell>
                                    <TableCell><strong>Server Version</strong></TableCell>
                                    <TableCell><strong>Client Version</strong></TableCell>
                                    <TableCell align="center"><strong>Compatible</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedCompatibleBuilds.map((record, index) => {
                                    const serverBuild = builds.find(b => b.id === record.server_build);
                                    const clientBuild = builds.find(b => b.id === record.client_build);
                                    const rowId = `${record.server_build}-${record.client_build}-${index}`;
                                    const isExpanded = expandedRow === rowId;
                                    const relatedBuilds = getRelatedCompatibleBuilds(record);
                                    
                                    return (
                                        <>
                                            <TableRow 
                                                key={rowId}
                                                hover
                                                sx={{ cursor: record.compatible ? 'pointer' : 'default' }}
                                                onClick={() => {
                                                    if (record.compatible) {
                                                        setExpandedRow(isExpanded ? null : rowId);
                                                    }
                                                }}
                                            >
                                                <TableCell>
                                                    {record.compatible && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setExpandedRow(isExpanded ? null : rowId);
                                                            }}
                                                        >
                                                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {serverBuild?.version_number || record.server_build}
                                                </TableCell>
                                                <TableCell>
                                                    {clientBuild?.version_number || record.client_build}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={record.compatible ? 'Yes' : 'No'}
                                                        color={record.compatible ? 'success' : 'default'}
                                                        size="small"
                                                        variant={record.compatible ? 'filled' : 'outlined'}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                            {record.compatible && (
                                                <TableRow>
                                                    <TableCell 
                                                        colSpan={4} 
                                                        sx={{ 
                                                            py: 0,
                                                            borderBottom: isExpanded ? '1px solid rgba(224, 224, 224, 1)' : 'none'
                                                        }}
                                                    >
                                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                            <Box sx={{ p: 2, bgcolor: 'background.default', width: '100%', boxSizing: 'border-box' }}>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, fontSize: '0.9rem' }}>
                                                                    Compatible Builds
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', gap: 2, width: '100%', boxSizing: 'border-box' }}>
                                                                    {/* Left Pane - Server Builds Compatible with Current Client */}
                                                                    <Box sx={{ flex: '1 1 50%', minWidth: 0, maxWidth: '50%' }}>
                                                                        <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1, color: 'text.secondary', fontSize: '0.8rem' }}>
                                                                            Server Builds (For Client {clientBuild?.version_number || ''})
                                                                        </Typography>
                                                                        {relatedBuilds.compatibleServerBuilds.length > 0 ? (
                                                                            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                                                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                                    {relatedBuilds.compatibleServerBuilds.map((build) => (
                                                                                        <BuildCard key={build.id} build={build} onClick={handleBuildClick} />
                                                                                    ))}
                                                                                </Box>
                                                                            </Box>
                                                                        ) : (
                                                                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
                                                                                No other server builds
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                    
                                                                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                                                                    
                                                                    {/* Right Pane - Client Builds Compatible with Current Server */}
                                                                    <Box sx={{ flex: '1 1 50%', minWidth: 0, maxWidth: '50%' }}>
                                                                        <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1, color: 'text.secondary', fontSize: '0.8rem' }}>
                                                                            Client Builds (For Server {serverBuild?.version_number || ''})
                                                                        </Typography>
                                                                        {relatedBuilds.compatibleClientBuilds.length > 0 ? (
                                                                            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                                                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                                    {relatedBuilds.compatibleClientBuilds.map((build) => (
                                                                                        <BuildCard key={build.id} build={build} onClick={handleBuildClick} />
                                                                                    ))}
                                                                                </Box>
                                                                            </Box>
                                                                        ) : (
                                                                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
                                                                                No other client builds
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Summary */}
                {sortedCompatibleBuilds.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            Showing {sortedCompatibleBuilds.length} of {compatibleBuilds.length} compatible build combination(s).
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Preview Modal */}
            <Modal 
                open={previewOpen && !!selectedBuild}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleClosePreview();
                    }
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                }}
            >
                <Box 
                    sx={{
                        position: 'relative',
                        width: "90%",
                        maxWidth: "900px",
                        maxHeight: "90vh",
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        outline: 'none',
                        overflow: 'auto',
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <IconButton
                        onClick={handleClosePreview}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            zIndex: 1,
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {selectedBuild && <PreviewBuild build={selectedBuild} />}
                </Box>
            </Modal>
        </Container>
    );
}
